import React from "react";

interface CursorLinesProps {
  isEditMode: boolean;
  cursorX: number | null;
  playbackCursorX: number | null;
  lockedCursorX: number | null;
  viewBoxHeight: number;
  isVideoLinked: boolean;
  isVideoPlaying: boolean;
  /**
   * Map live-follow: keep the playback cursor rendered even when the video is
   * momentarily stalled (caught up to the newest clip, isVideoPlaying false), so
   * the live position marker stays visible. Off elsewhere → unchanged behavior.
   */
  isLive?: boolean;
}

export const CursorLines: React.FC<CursorLinesProps> = ({
  isEditMode,
  cursorX,
  playbackCursorX,
  lockedCursorX,
  viewBoxHeight,
  isVideoLinked,
  isVideoPlaying,
  isLive = false,
}) => {
  const isLinkedAndPlaying = isVideoLinked && isVideoPlaying;
  // In live-follow the marker tracks the video position whenever linked, so it
  // doesn't blink out during the brief caught-up-to-live stalls between clips.
  const showPlaybackCursor = isLinkedAndPlaying || (isLive && isVideoLinked);

  return (
    <>
      {/* Hover indicator: thin green line - always visible on hover */}
      {cursorX !== null && (
        <line
          x1={cursorX}
          x2={cursorX}
          y1={0}
          y2={viewBoxHeight}
          stroke="#22c55e"
          strokeWidth={1}
          strokeOpacity={0.6}
          pointerEvents="none"
        />
      )}

      {/* Playback cursor: red line when video is linked and playing — or, in live
          mode, whenever linked (so it survives brief caught-up stalls). */}
      {showPlaybackCursor && playbackCursorX !== null && (
        <line
          x1={playbackCursorX}
          x2={playbackCursorX}
          y1={0}
          y2={viewBoxHeight}
          stroke="#ef4444"
          strokeWidth={3}
          pointerEvents="none"
        />
      )}

      {/* Locked (frozen) cursor: orange thick line - always visible when frozen */}
      {lockedCursorX !== null && (
        <line
          x1={lockedCursorX}
          x2={lockedCursorX}
          y1={0}
          y2={viewBoxHeight}
          stroke="#f97316"
          strokeWidth={isLinkedAndPlaying ? 2 : 3}
          strokeOpacity={isLinkedAndPlaying ? 0.7 : 1}
        />
      )}
    </>
  );
};
